import React, { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Form, Search } from 'semantic-ui-react'
import { ORGANIZATIONS_AUTOCOMPLETE } from '../../graphql/organization'

const MIN_CHARACTERS = 3
const DEBOUNCE_MS = 300

const Autocomplete = ({
  context,
  readOnly,
  value,
  onChange,
  onSelect,
  openCreateModal,
  ...rest
}) => {
  const [query, setQuery] = useState(value || '')

  useEffect(() => {
    const timeout = setTimeout(() => setQuery(value || ''), DEBOUNCE_MS)
    return () => clearTimeout(timeout)
  }, [value])

  const skip = readOnly || query.trim().length < MIN_CHARACTERS

  const { data, previousData, loading } = useQuery(ORGANIZATIONS_AUTOCOMPLETE, {
    variables: { query },
    skip,
    context: { skipErrorDisplay: true },
  })

  const results = useMemo(() => {
    if (skip) return []

    // Keep the last results on screen while the next query loads, so the
    // dropdown does not empty out between keystrokes.
    const organizations =
      (data || previousData)?.organizationsAutocomplete || []
    const autocompleteResults = organizations.map((organization) => ({
      id: organization._id,
      title: organization.name,
      organization,
    }))
    if (openCreateModal) {
      autocompleteResults.push({
        title: `Click here to add ${value}`,
        onClick: openCreateModal,
      })
    }
    return autocompleteResults
  }, [skip, data, previousData, openCreateModal, value])

  const setSelection = (query, organizationId, organization = null) => {
    onChange(query)
    onSelect({ organizationId, organization })
  }

  if (readOnly) {
    return (
      <Form.Input
        {...rest}
        icon={{
          name: 'close',
          link: true,
          color: 'red',
          onClick: () => setSelection('', null),
        }}
        value={value}
        className="autocomplete-readOnly"
      />
    )
  }

  return (
    <Form.Field required={rest.required} width={rest.width} error={rest.error}>
      <label>{rest.label}</label>
      <Search
        fluid
        showNoResults={false}
        defaultOpen={context === 'OpenAlex'}
        minCharacters={MIN_CHARACTERS}
        loading={loading}
        placeholder={rest.placeholder}
        onSearchChange={(e, { value }) => onChange(value)}
        onResultSelect={(e, { result }) =>
          setSelection(result.title, result.id, result.organization)
        }
        results={results}
        value={value}
      />
    </Form.Field>
  )
}

export default Autocomplete
